import { Routes, Route, useNavigate, Navigate, useParams, useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client/react/hooks";
import { CircularProgress } from "@mui/material";
import { Analytics as VercelAnalytics } from "@vercel/analytics/react";
import "./App.css";
import Login from "./Modules/Login/Login";
import RouteWrapper from "./Route/index";
import CatererDetails from "./screens/caterer-details";
import { GroupOrderShell } from "./screens/group-order/group-order-shell";
import { MyGroupOrders } from "./screens/group-order/my-group-orders";
import { AdminGroupOrders } from "./screens/group-order/admin-group-orders";
import { AdminGroupOrderView } from "./screens/group-order/admin-group-order-view";
import { GroupOrdersLayout } from "./screens/group-order/group-orders-layout";
import { GroupCatererDetails, GroupCatererList } from "./screens/group-order/group-caterer-list";
import MenuList from "./Modules/Menu";
import Location from "./Modules/Location/Location";
import CatererListingScreen from "./Modules/Admin/catererListingScreen";
import CatererDetailsScreen from "./Modules/Admin/catererDetailsScreen";
import Register from "./Modules/Registration";
import CatererList from "./screens/caterer-list/caterer-list";
import ReviewOrder from "./screens/checkout";
import CheckoutSuccess from "./screens/checkout-success";
import ReferFriend from "./Modules/ReferFriend/index";
import CustomerList from "./Modules/Admin/customerList";
import CategoryList from "./Modules/Admin/Categories/categoryList";
import ViewMenu from "./Modules/Menu/MenuAction/ViewMenu";
import CustomerDetails from "./Modules/Admin/CustomerListDetails";
import Home from "./screens/home";
import Summary from "./Modules/Summary";
import PromoCodes from "./Modules/Admin/Promocode/promocode";
import CuisinesList from "./Modules/Admin/Cuisines";
import AdminLogin from "./Modules/Login/AdminLogin";
import ResetPassword from "./Modules/Login/ResetPassword";
import LocationDetails from "./screens/caterer-profile/caterer-profile";
import { useReactiveVar } from "@apollo/client";
import { authVar, userDeatils, toggleSnackbar } from "./ReactiveVariables/index";
import "./styles/style.css";
import { CURRENT_USER_DETAILS } from "./Graphql/queries";
import Orders from "./screens/orders";
import PendingOrders from "./screens/orders/pending-orders";
import CustomerProfile from "./Modules/CustomerProfile/customerProfile";
import TaxExemption from "./Modules/CustomerProfile/taxExemption";
import AdminUserList from "./Modules/Admin/AdminUser/adminUserList";
import ViewOrder from "./screens/view-order";
import CaterCash from "./Modules/CaterCash";
import Payments from "./Modules/Payments/payment";
import AddressList from "./screens/address-list";
import { ViewAddress } from "./screens/view-address";
import PaymentAdmin from "./Modules/Payments/paymentAdmin";
import PaymentAdminDetail from "./Modules/Payments/paymentAdminDetail";
import Reviews from "./Modules/Admin/Review/Reviews";
import Reports from "./Modules/Admin/Reports/reports";
import Settings from "./Modules/Admin/Settings/settings";
import AboutUs from "./Modules/Home/HeaderLinks/About";
import TermsCondition from "./Modules/Home/HeaderLinks/terms-condition";
import PrivacyPolicy from "./Modules/Home/HeaderLinks/privacy-policy";
import DashBoard from "./Modules/Admin/Dashboard/dashboard";
import Analytics from "./Modules/Admin/Analytics/analytics";
import Faq from "./Modules/Home/HeaderLinks/faq";
import Contact from "./Modules/Home/HeaderLinks/contact";
import Caterers from "./Modules/Home/HeaderLinks/caterers";
import AccessDenied from "./Modules/Common/accessDenied";
import EditOrderManual from "./screens/edit-order-manual";
import SortCategory from "./Modules/Menu/MenuAction/categorySort";
import MenuImportReview from "./Modules/Menu/MenuImportReview";
import { useCookies } from "react-cookie";
import React, { useEffect } from "react";
import { useLogout } from "./utils/hooks/use-logout";
import { ROUTES } from "@constants/routes.constants";
import StripeOnboardingComplete from "./Modules/StripeOnboarding/StripeOnboardingComplete";
import StripeOnboardingRefresh from "./Modules/StripeOnboarding/StripeOnboardingRefresh";
import StripeSetupPage from "./Modules/StripeOnboarding/StripeSetupPage";
import { useGroupDraftGuard } from "./services/group-order/use-group-draft-guard";

function App() {
  const navigate = useNavigate();
  const user = useReactiveVar(userDeatils);
  const auth = useReactiveVar(authVar);
  const token = localStorage.getItem("@authToken");
  const [cookies, setCookie, removeCookie] = useCookies([
    "user",
    "image",
    "userType",
    "@authToken",
  ]);
  const location = useLocation();
  useGroupDraftGuard();
  const path = location.pathname;
  let shortToken = cookies["@authToken"];
  const dynamicValue = path.split("/")[2]; // Assuming the dynamic value is always the third segment of the path

  // Additions for redirect logic on login
  const queryParams = new URLSearchParams(location.search);
  const redirectPath = location.state?.url || queryParams.get("redirect");

  const apiUrl = process.env.REACT_APP_API_URL + "/graphql";
  let currentLocation = location.pathname; // Use React Router's pathname instead of window.location.pathname
  const generateExcludedPaths = (id) => {
    return [
      "/",
      "/customerlogin",
      "/admin",
      "/login",
      "/register",
      `/register/${dynamicValue}`,
      "/terms-and-conditions",
      "/privacy-policy",
      "/faq",
      "/contact",
      "/caterer",
      "/caterers",
      `/caterers/${dynamicValue}`,
      "/resetPasswordToken",
      `/resetPasswordToken/${dynamicValue}`,
      "/about",
      "/orderconfirmed",
    ];
  };

  const excludedPaths = generateExcludedPaths(dynamicValue);
  const shouldRedirect =
    !excludedPaths.includes(currentLocation) && !currentLocation.startsWith("/group/");

  const options = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      // If you have an authentication token, include it in the headers
      Authorization: `${token ?? shortToken}`,
    },
    body: JSON.stringify({
      query:
        "query GetUserStatus {\n  getUserStatus {\n    message\n    status\n    __typename\n  }\n}",
    }),
  };

  const { data, loading } = useQuery(CURRENT_USER_DETAILS, {
    fetchPolicy: "network-only",
    onCompleted: (res) => {
      if (res.currentUserDetails?.catererId === "" || res.currentUserDetails?.catererId) {
        userDeatils({ data: res });
        const isProduction = window.location.hostname.includes("caterplace.com");
        const cookieOptions = {
          path: "/",
          ...(isProduction && { domain: ".caterplace.com" }),
        };

        setCookie("user", res?.currentUserDetails?.name, cookieOptions);
        setCookie("image", res?.currentUserDetails?.imageUrl, cookieOptions);
        setCookie("userType", res?.currentUserDetails?.userType, cookieOptions);
      }
      authVar({
        loaded: true,
        auth: true,
      });
    },
  });

  const userType =
    user?.data?.login?.userType ||
    user?.data?.currentUserDetails?.userType ||
    user?.data?.createUserIdentity?.userType ||
    user?.data?.createCaterer?.userType;

  const { onLogout } = useLogout({
    userType,
    logoutCallback: () =>
      toggleSnackbar({
        status: true,
        message: "Your session has expired. Please log back in to continue.",
        variant: "error",
      }),
  });

  useEffect(() => {
    if (shouldRedirect) {
      fetch(apiUrl, options)
        .then((response) => response.json())
        .then((data) => {
          // Handle the response data
          if (currentLocation.includes("home")) {
            navigate(ROUTES.root);
          } else if (data?.data?.getUserStatus === null) {
            onLogout();
          }
        })
        .catch((error) => {
          // Handle any errors
          console.error("error", error);
        });
    }
  }, [shouldRedirect, location]);

  const customerUserType = {
    user: "Customer",
  };

  return (
    <>
      <Routes>
        <Route path="/" element={<RouteWrapper />}>
          <Route index element={<Home />} />
          {userType === "Caterer" && <Route path="menu" element={<MenuList />} />}
          {userType === "Caterer" && <Route path="sort-category/:id" element={<SortCategory />} />}
          {userType === "Caterer" && (
            <Route path="menu-import/:jobId/review" element={<MenuImportReview />} />
          )}
          {userType === "Caterer" && <Route path="location" element={<Location />} />}
          {userType === "Caterer" && <Route path="profile" element={<LocationDetails />} />}
          {userType !== "Customer" && <Route path="menu/:id" element={<ViewMenu />} />}
          {userType === "Admin" && <Route path="customers" element={<CustomerList />} />}
          {userType === "Admin" && <Route path="customers/:id" element={<CustomerDetails />} />}
          {userType === "Admin" && (
            <Route path="admincatererlist" element={<CatererListingScreen />} />
          )}
          {userType === "Admin" && (
            <Route path="admincatererlist/:id" element={<CatererDetailsScreen />} />
          )}
          {(userType === "Admin" || userType === "Customer") && (
            <Route path="revieworder/:id" element={<ReviewOrder />} />
          )}
          {userType === "Customer" && (
            <Route path="group-orders/:groupOrderId/caterers" element={<GroupOrderShell mode="rep" />}>
              <Route index element={<GroupCatererList />} />
              <Route path=":id" element={<GroupCatererDetails />} />
            </Route>
          )}
          {(userType === "Customer" || userType === "Admin") && (
            <Route
              path="group-orders"
              element={
                <GroupOrdersLayout>
                  {userType === "Admin" ? <AdminGroupOrders /> : <MyGroupOrders />}
                </GroupOrdersLayout>
              }
            />
          )}
          {userType === "Admin" && (
            <Route
              path="group-orders/:groupOrderId/view"
              element={
                <GroupOrdersLayout>
                  <AdminGroupOrderView />
                </GroupOrdersLayout>
              }
            />
          )}
          {userType === "Admin" && <Route path="category" element={<CategoryList />} />}
          {userType === "Admin" && <Route path="cuisines" element={<CuisinesList />} />}
          {userType === "Admin" && <Route path="adminuser" element={<AdminUserList />} />}
          <Route path="orders" element={<Orders />} />
          <Route path="pendingOrders" element={<PendingOrders />} />
          {userType === "Caterer" && <Route path="summary" element={<Summary />} />}
          {userType === "Caterer" && <Route path="stripe-setup" element={<StripeSetupPage />} />}
          <Route path="orders/:id" element={<ViewOrder />} />
          {userType === "Customer" && <Route path="profile" element={<CustomerProfile />} />}
          {userType === "Customer" && <Route path="taxes" element={<TaxExemption />} />}
          {userType === "Admin" && <Route path="promocodes" element={<PromoCodes />} />}
          {userType !== "Caterer" && <Route path="referFriend" element={<ReferFriend />} />}
          {userType !== "Caterer" && <Route path="CaterCash" element={<CaterCash />} />}
          {userType !== "Customer" && (
            <Route
              path="payments"
              element={userType !== "Admin" ? <Payments /> : <PaymentAdmin />}
            />
          )}
          {userType === "Admin" && <Route path="payments/:id" element={<PaymentAdminDetail />} />}
          {userType === "Customer" && <Route path="address" element={<AddressList />} />}
          {userType === "Admin" && <Route path="reviews" element={<Reviews />} />}
          {userType === "Customer" && <Route path="address/:id" element={<ViewAddress />} />}
          {userType === "Admin" && <Route path="reports" element={<Reports />} />}
          {userType === "Admin" && <Route path="settings" element={<Settings />} />}
          {userType === "Admin" && <Route path="dashboard" element={<DashBoard />} />}
          {userType === "Admin" && <Route path="analytics" element={<Analytics />} />}
          {userType === "Admin" && (
            <Route path="edit-order/:id" element={<EditOrderManual />} />
          )}
        </Route>
        <Route path="resetPasswordToken/:id" element={<ResetPassword />} />
        <Route path="caterers" element={<CatererList />} />
        <Route path="caterers/:id" element={<CatererDetails />} />
        <Route path="group/:token" element={<GroupOrderShell mode="office" />}>
          <Route index element={<GroupCatererList />} />
          <Route path=":id" element={<GroupCatererDetails />} />
        </Route>
        <Route path="orderconfirmed" element={<CheckoutSuccess />} />
        <Route
          path="login"
          element={
            auth?.auth
              ? redirectPath
                ? <Navigate to={redirectPath} replace />
                : userType === "Admin"
                  ? <Navigate to="/dashboard" replace />
                  : userType === "Caterer"
                    ? <Navigate to="/summary" replace />
                    : <Navigate to={ROUTES.root} replace />
              : <Login />
          }
        />
        <Route
          path="admin"
          element={
            auth?.auth
              ? userType === "Admin"
                ? <Navigate to="/dashboard" replace />
                : <Navigate to="/" replace />
              : <AdminLogin />
          }
        />
        <Route path="register" element={<Register />} />
        <Route
          path="register/:token"
          state={customerUserType}
          element={<Register userType={customerUserType} />}
        />
        <Route
          path="register/customer"
          element={<Register userType={{ user: 'Customer' }} />}
        />
        <Route
          path="register/caterer"
          element={<Register userType={{ user: 'Caterer' }} />}
        />
        <Route path="caterer/onboarding/complete" element={<StripeOnboardingComplete />} />
        <Route path="caterer/onboarding/refresh" element={<StripeOnboardingRefresh />} />
        <Route path="about" element={<AboutUs />} />
        <Route path="terms-and-conditions" element={<TermsCondition />} />
        <Route path="privacy-policy" element={<PrivacyPolicy />} />
        <Route path="faq" element={<Faq />} />
        <Route path="contact" element={<Contact />} />
        <Route path="caterer" element={<Caterers />} />
        <Route path="*" element={<AccessDenied />} />
      </Routes>
      <VercelAnalytics />
    </>
  );
}

export default App;